import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { api } from "@shared/routes";
import { apiUrl } from "@/lib/api-url";
import { type LoginInput, type RegisterInput } from "@shared/routes";

const AUTH_QUERY_KEY = ["auth-user"];

function normalizeAuthUser(input: any, fallbackEmail = "", fallbackUsername = "") {
  const source =
    input && typeof input === "object" && !Array.isArray(input)
      ? input.user && typeof input.user === "object" && !Array.isArray(input.user)
        ? input.user
        : input
      : {};

  const email = typeof source.email === "string" && source.email.trim() ? source.email.trim() : fallbackEmail;
  const username =
    typeof source.username === "string" && source.username.trim()
      ? source.username.trim()
      : email
        ? email.split("@")[0]
        : fallbackUsername;
  const role = typeof source.role === "string" && source.role.trim() ? source.role.trim() : "user";
  const avatarUrl =
    typeof source.avatarUrl === "string" || source.avatarUrl === null
      ? source.avatarUrl ?? null
      : typeof source.avatar_url === "string" || source.avatar_url === null
        ? source.avatar_url ?? null
        : null;

  return {
    id: typeof source.id === "number" && Number.isFinite(source.id) ? source.id : 1,
    username,
    email,
    role,
    avatarUrl,
  };
}

async function loadCurrentUser() {
  const response = await fetch(apiUrl(api.auth.me.path), { credentials: "include" });
  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.message || "Failed to load current user.");
  }
  const payload = await response.json();
  return normalizeAuthUser(payload);
}

function getRedirectTarget() {
  if (typeof window === "undefined") return "/";
  const redirect = new URLSearchParams(window.location.search).get("redirect");
  if (!redirect) return "/";
  return redirect.startsWith("/") ? redirect : "/";
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const userQuery = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: loadCurrentUser,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginInput) => {
      const response = await fetch(apiUrl(api.auth.login.path), {
        method: api.auth.login.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
        credentials: "include",
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Login failed.");
      }
      return normalizeAuthUser(payload, credentials.email, "");
    },
    onSuccess: (user) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
      toast({ title: "Welcome back!", description: "Successfully logged in." });
      setLocation(getRedirectTarget());
    },
    onError: (error: Error) => {
      toast({ title: "Login Failed", description: error.message, variant: "destructive" });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterInput) => {
      const response = await fetch(apiUrl(api.auth.register.path), {
        method: api.auth.register.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Registration failed.");
      }
      return normalizeAuthUser(payload, data.email, data.username);
    },
    onSuccess: (user) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
      toast({ title: "Account created", description: "Welcome to DRABPE Directory!" });
      setLocation(getRedirectTarget());
    },
    onError: (error: Error) => {
      toast({ title: "Registration Failed", description: error.message, variant: "destructive" });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(apiUrl(api.auth.logout.path), {
        method: api.auth.logout.method,
        credentials: "include",
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Logout failed.");
      }
      return payload;
    },
    onSuccess: () => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
      queryClient.clear();
      toast({ title: "Logged out", description: "Successfully logged out." });
      setLocation("/");
    },
  });

  return {
    user: userQuery.data,
    isLoading: userQuery.isLoading,
    login: loginMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    register: registerMutation.mutate,
    isRegistering: registerMutation.isPending,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
    error:
      (loginMutation.error as Error | undefined)?.message ||
      (registerMutation.error as Error | undefined)?.message ||
      (logoutMutation.error as Error | undefined)?.message ||
      null,
  };
}
